<template>
  <form name="publications-form" class="PublicationForm" @submit.prevent="$emit('save')">
    <BLayoutExpansion title="Основное" class="my-4">
      <v-row dense>
        <v-col md="8" lg="6">
          <BTextarea
            v-model="form.name"
            validate="required"
            label="Название"
            :disabled="isView"
            :skeleton="skeleton"
            rows="2"
            name="name"
            autofocus
            counter
          />
        </v-col>

        <v-col v-if="isRedirectLKButtonShow" md="4" lg="6" class="align-center justify-end d-flex">
          <router-link
            :to="linkTo"
            target="_blank"
            class="PublicationForm__link"
            @click.native.stop
          >
            <IconLink
              tooltip="Открыть публикацию в новой вкладке"
              :tooltip-attrs="{ left: true }"
              class="PublicationForm__link-icon"
            />
          </router-link>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col md="8" lg="6">
          <BAutocompleteCommon
            v-model="form.procedure_id"
            :skeleton="skeleton"
            :disabled="isView"
            :return-object="false"
            $type="procedure"
            name="procedure_id"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col v-if="!isCreate" cols="12">
          <v-skeleton-loader :loading="skeleton" type="sentences">
            <div class="d-flex align-center">
              <v-checkbox
                v-model="data.is_redirect_to_self"
                disabled
                dense
                :ripple="false"
                hide-details
                class="ma-0 pa-0"
                name="is_redirect_to_self"
              >
                <template #label>
                  <span>Перенаправление на публикацию</span>
                  <b class="ml-1">{{ data.is_redirect_to_self ? 'включено' : 'выключено' }}</b>
                </template>
              </v-checkbox>

              <v-btn
                v-if="!isView"
                :value="data.is_redirect_to_self"
                :loading="isLoadingRedirect"
                outlined
                dense
                small
                class="ml-2 caption"
                @click="$emit('redirect', toggleLoadingRedirect)"
              >
                {{ data.is_redirect_to_self ? 'Выключить' : 'Включить' }}
              </v-btn>
            </div>
          </v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col md="8" lg="6">
          <BAutocompleteCommon
            v-model="form.topic_ids"
            validate="required"
            :skeleton="skeleton"
            :disabled="isView"
            :return-object="false"
            $type="topic"
            multiple
            small-chips
            name="topic_ids"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" md="4">
          <BTextFieldDate
            v-model="form.start_at"
            validate="required"
            :max="form.end_at"
            :skeleton="skeleton"
            :disabled="isView"
            label="Дата начала размещения в ЛК"
            placeholder="Дата начала"
            name="start_at"
            datetime
            with-buttons
          />
        </v-col>

        <v-col cols="12" md="4">
          <BTextFieldDate
            v-model="form.end_at"
            :validate="endAtRules"
            :min="form.start_at"
            :skeleton="skeleton"
            :disabled="isView"
            clearable
            label="Дата окончания размещения в ЛК"
            placeholder="Дата окончания"
            name="end_at"
            datetime
            with-buttons
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" md="4" lg="3">
          <BTextFieldDate
            v-model="form.published_at"
            :skeleton="skeleton"
            :disabled="isView"
            :max="form.end_at"
            validate="required"
            label="Дата публикации"
            placeholder="Введите дату"
            name="published_at"
            datetime
            with-buttons
          />
        </v-col>

        <v-col v-if="!isCreate" cols="12" md="4" lg="3">
          <BTextField
            v-if="form.archived_at || isView"
            :value="form.archived_at | dateFormatter('D.MM.YYYY HH:mm')"
            :skeleton="skeleton"
            label="В архиве с"
            disabled
          />

          <v-skeleton-loader v-else :loading="skeleton" type="button">
            <v-btn
              :loading="isLoadingArchived"
              depressed
              @click="$emit('archived', toggleLoadingArchived)"
              v-text="'Архивировать'"
            />
          </v-skeleton-loader>
        </v-col>

        <v-col v-if="!isCreate && !!form.archived_at" cols="12" md="4" lg="3">
          <v-skeleton-loader :loading="skeleton" type="button">
            <v-btn
              :loading="isLoadingArchived"
              depressed
              @click="$emit('unarchived', toggleLoadingArchived)"
              v-text="'Разархивировать'"
            />
          </v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12">
          <v-skeleton-loader :loading="skeleton" type="sentences">
            <v-checkbox
              v-model="form.is_public"
              :disabled="isView"
              label="Показывать всем?"
              dense
              hide-details
              class="ma-0 pa-0"
              name="is_public"
              @change="form.participants_list_id = null"
            />
          </v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12">
          <v-skeleton-loader :loading="skeleton" type="sentences">
            <v-checkbox
              v-model="form.has_recommendation"
              :disabled="isView"
              label="Рекомендация?"
              dense
              hide-details
              class="ma-0 pa-0"
              name="has_recommendation"
            />
          </v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col v-if="form.has_recommendation">
          <v-skeleton-loader :loading="skeleton" type="sentences">
            <v-select
              v-model="form.recommendation_algorithm"
              :items="algorithmOptions"
              :disabled="isView"
              item-value="key"
              item-text="text"
              validate="required"
              label="Выберите алгоритм для генерации рекомендаций."
              dense
            />
          </v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row dense class="mt-4">
        <v-col md="10" lg="8">
          <BEditor
            v-model="form.description"
            validate="required"
            data-vv-as="Описание"
            placeholder="Введите описание"
            :disabled="isView"
            :skeleton="skeleton"
            min-height="200px"
            name="description"
          />
        </v-col>
      </v-row>

      <v-row v-if="!isCreate" dense>
        <v-col :cols="!skeleton && 'auto'">
          <BTextField
            :value="data.created_at | dateFormatter"
            :skeleton="skeleton"
            label="Создан"
            disabled
          />
        </v-col>

        <v-col :cols="!skeleton && 'auto'">
          <BTextField
            :value="data.updated_at | dateFormatter"
            :skeleton="skeleton"
            label="Обновлен"
            disabled
          />
        </v-col>
      </v-row>

      <slot name="save-menu" />
    </BLayoutExpansion>

    <button type="submit" class="d-none" />
  </form>
</template>

<script>
import { isEmpty } from 'lodash'
import BLayoutForm from '@/components/layouts/BLayoutForm.vue'
import BLayoutExpansion from '@/components/layouts/BLayoutExpansion.vue'
import BTextarea from '@/components/forms/BTextarea.vue'
import BTextField from '@/components/forms/BTextField.vue'
import IconLink from '@/components/lk/icons/LKIconLink.vue'
import BTextFieldDate from '@/components/forms/BTextFieldDate.vue'
import BAutocompleteCommon from '@/components/forms/BAutocompleteCommon.vue'
import { LK_PUBLICATIONS_ROUTES } from '@/views/LKPublications/utils'
import { TopicsApi } from '@/api/resources'
import { Adapter } from '@/store/createCrudModule'


const BEditor = () => import(
  /* webpackChunkName: "BEditor" */
  '@/components/forms/BEditor.vue'
)

const createDefaultData = () => ({
  // id
  name: '',
  // slug
  description: '.',
  start_at: void 0,
  end_at: void 0,
  published_at: void 0,
  participants_list_id: null,
  is_public: false,
  topic_ids: [],
  // slug
  // archived_at
  publication_id: null,
})

export default {
  name: 'PublicationsForm',
  components: {
    IconLink,
    BLayoutExpansion,
    BTextarea,
    BTextField,
    BEditor,
    BTextFieldDate,
    BAutocompleteCommon,
  },
  extends: BLayoutForm,
  props: {
    editable: Boolean,
    skeleton: Boolean,
    loading: Boolean,
    data: {
      type: Object,
      required: true,
      default: createDefaultData,
    },
  },
  data: () => ({
    isLoadingArchived: false,
    isLoadingRedirect: false,
    isLoadingCopy: false,
    counter: 0,
    topics: {},
    procedures: {},
    algorithmOptions: [
      { text: 'Рисковый профиль школы 2023', key: 'rpsh2023' },
      { text: 'Рисковый профиль школы 2024', key: 'rpsh2024' },
      { text: 'Рисковый профиль школы 2026 (Национальный)', key: 'rpsh_v3_nat' },
      { text: 'Рисковый профиль школы 2026 (НовРег)', key: 'rpsh_v3_reg' },
      { text: 'Рисковый профиль школы 2026 (Общий)', key: 'rpsh_v3_common' },
    ], // Варианты алгоритмов
  }),
  computed: {
    endAtRules() {
      const { start_at, end_at, published_at } = this.form
      return {
        afterBoth: {
          value: end_at,
          first: start_at,
          firstField: 'Дата начала размещения в ЛК',
          second: published_at,
          secondField: 'Дата публикации',
        },
      }
    },
    isRedirectLKButtonShow() {
      return !isEmpty(this.topics)
          && Array.isArray(this.data.topic_ids)
          && this.data.topic_ids.length > 0
    },
    linkTo() {
      if (!this.isRedirectLKButtonShow) {
        return {}
      }

      const { data } = this.topics

      if (Array.isArray(data)) {
        const idx = data.findIndex(({ id }) => id === this.data.topic_ids[0])
        if (idx < 0) {
          return {}
        }

        return {
          name: LK_PUBLICATIONS_ROUTES.VIEW,
          params: {
            topic_slug: data[idx].slug,
            slug: this.data.slug,
          },
        }
      }
      return {}
    },
  },
  watch: {
    'form.has_recommendation': {
      handler(newValue) {
        if (!newValue) {
          this.form.recommendation_algorithm = null
        }
      },
    },
  },
  async created() {
    this.topics = await new Adapter(new TopicsApi()).getAll()
  },
  methods: {
    toggleLoadingArchived() {
      this.isLoadingArchived = !this.isLoadingArchived
      return this.toggleLoadingArchived
    },
    toggleLoadingRedirect() {
      this.isLoadingRedirect = !this.isLoadingRedirect
      return this.toggleLoadingRedirect
    },
  },
}
</script>

<style lang="scss" scoped>
.PublicationForm {
  &__link {
    display: block;
    margin: 0 4px;
    color: $webflow-color-black;
    cursor: pointer;
    text-decoration-line: none;

    &:hover {
      opacity: 0.75;
    }
  }

  &__link-icon {
    pointer-events: none;
  }
}
</style>
